import { isValidElement, createElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Search, Sparkles, FileText, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

/**
 * Helper to render an icon that may be:
 * - A React element (<Icon />)
 * - A component type (function or forwardRef)
 */
function renderIcon(icon: React.ReactNode | LucideIcon, className: string): React.ReactNode {
  // Already a rendered element
  if (isValidElement(icon)) {
    return icon;
  }
  // Component type (function or forwardRef object)
  if (
    typeof icon === 'function' ||
    (typeof icon === 'object' && icon !== null && '$$typeof' in icon)
  ) {
    // Use createElement to avoid JSX type issues with forwardRef components
    return createElement(icon as React.ComponentType<{ className?: string }>, { className });
  }
  return null;
}

/** Preset types for common empty states */
export type EmptyStateType = 'no-results' | 'no-tasks' | 'no-notes' | 'all-done' | 'custom';

export type EmptyStateProps = {
  /** Preset type - provides default icon, title, description */
  type?: EmptyStateType;
  /** Icon component (lucide-react) - overrides type default */
  icon?: React.ReactNode | LucideIcon;
  /** Main heading - overrides type default */
  title?: string;
  /** Optional description text - overrides type default */
  description?: string;
  /** Primary action button */
  primary?: { label: string; onClick: () => void } | null;
  /** Secondary action button */
  secondary?: { label: string; onClick: () => void } | null;
  /** Shorthand for primary action (compat with shared/EmptyState) */
  action?: { label: string; onClick: () => void };
  /** Optional learn more link */
  learnMoreHref?: string;
  /** Additional CSS classes */
  className?: string;
  /** Compact variant for panels */
  variant?: 'default' | 'compact' | 'inline';
  /** Optional emoji to display after title */
  emoji?: string;
  /** i18n message key (compat with primitives/EmptyState) */
  messageKey?: string;
  /** i18n namespace (compat with primitives/EmptyState) */
  namespace?: string;
};

/** Default configurations for preset types */
const typeDefaults: Record<
  Exclude<EmptyStateType, 'custom'>,
  { icon: LucideIcon; title: string; description: string }
> = {
  'no-results': {
    icon: Search,
    title: 'No results found',
    description: 'Try a different search term',
  },
  'no-tasks': {
    icon: Sparkles,
    title: 'All done! No tasks yet',
    description: "Press '+ New Task' to get started",
  },
  'no-notes': {
    icon: FileText,
    title: 'No notes yet',
    description: 'Create your first note to get started',
  },
  'all-done': {
    icon: CheckCircle,
    title: 'All caught up!',
    description: "You've completed everything on your list",
  },
};

/**
 * EmptyState Component
 *
 * Unified empty state component for consistent UX across all pages.
 * Shows when a list/collection has no data.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   icon={<Car className="size-6" />}
 *   title="No vehicles yet"
 *   description="Add your first vehicle to start tracking inventory."
 *   primary={{ label: "Add vehicle", onClick: handleAdd }}
 *   secondary={{ label: "Import CSV", onClick: handleImport }}
 *   learnMoreHref="/help/vehicles"
 * />
 * ```
 */
export function EmptyState({
  type = 'custom',
  icon,
  title,
  description,
  primary,
  secondary,
  action,
  learnMoreHref,
  className,
  variant = 'default',
  emoji,
  messageKey,
  namespace = 'common',
}: EmptyStateProps) {
  const { t } = useTranslation(namespace);
  const isCompact = variant === 'compact';
  const isInline = variant === 'inline';

  // Resolve type defaults
  const defaults = type !== 'custom' ? typeDefaults[type] : null;

  // Resolve final values (explicit props override type defaults)
  const IconComponent = icon ?? defaults?.icon;
  const finalTitle = title ?? (messageKey ? t(messageKey) : defaults?.title) ?? 'No items';
  const finalDescription = description ?? defaults?.description;

  // Support both 'primary' and 'action' props for compatibility
  const primaryAction = primary ?? action;

  return (
    <div
      className={cn(
        'rounded-2xl border bg-card/70 backdrop-blur shadow-sm',
        'flex flex-col items-center justify-center text-center',
        isCompact ? 'p-6' : 'p-8 sm:p-10',
        className
      )}
      role="status"
      aria-live="polite"
    >
      {/* Icon */}
      {IconComponent && (
        <div
          className={cn(
            'rounded-full bg-accent/10 text-accent flex items-center justify-center mb-4',
            isCompact || isInline ? 'size-10 p-2' : 'size-12 p-2.5'
          )}
          aria-hidden="true"
        >
          {renderIcon(IconComponent, cn(isCompact || isInline ? 'size-5' : 'size-6'))}
        </div>
      )}

      {/* Title */}
      <h3
        className={cn(
          'font-semibold text-foreground',
          isCompact || isInline ? 'text-base' : 'text-lg sm:text-xl'
        )}
      >
        {finalTitle}
        {emoji && ` ${emoji}`}
      </h3>

      {/* Description */}
      {finalDescription && (
        <p
          className={cn(
            'text-muted-foreground mt-2 max-w-md',
            isCompact || isInline ? 'text-xs' : 'text-sm'
          )}
        >
          {finalDescription}
        </p>
      )}

      {/* Actions */}
      {(primaryAction || secondary || learnMoreHref) && (
        <div
          className={cn(
            'flex flex-col sm:flex-row items-center gap-3',
            isCompact || isInline ? 'mt-4' : 'mt-6'
          )}
        >
          {/* Primary Button */}
          {primaryAction && (
            <Button
              onClick={primaryAction.onClick}
              size={isCompact || isInline ? 'sm' : 'default'}
              variant={isInline ? 'outline' : 'default'}
              className="w-full sm:w-auto"
            >
              {primaryAction.label}
            </Button>
          )}

          {/* Secondary Button */}
          {secondary && (
            <Button
              onClick={secondary.onClick}
              variant="ghost"
              size={isCompact ? 'sm' : 'default'}
              className="w-full sm:w-auto"
            >
              {secondary.label}
            </Button>
          )}

          {/* Learn More Link */}
          {learnMoreHref && (
            <Button
              asChild
              variant="ghost"
              size={isCompact ? 'sm' : 'default'}
              className="w-full sm:w-auto"
            >
              <a
                href={learnMoreHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5"
              >
                {t('accessibility.learnMore')}
                <ExternalLink className="size-3.5" aria-hidden="true" />
              </a>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
